import { Box, styled, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { LastRemediationAttempt, RecentFailureItem } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { FailureWhyButton } from '@/components/FailureDiagnostics/FailureWhyButton';
import { useTenants } from '@/services/tenants/useTenants';
import { failureLink } from './failureLinks';
import { FAILURE_TABLE_GRID_COLUMN, failureTableRowLayout } from './failureTableLayout';
import { RowRemediationButton } from './remediation/RowRemediationButton';

const KIND_LABELS: Record<string, string> = {
  asset: 'Asset',
  report: 'Report',
  'part-finder-report': 'Part Finder',
};

const Row = styled(Box)(({ theme }) => ({
  ...failureTableRowLayout,
  borderRadius: '6px',
  '&:hover': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
  },
}));

const NameCell = styled(Box)({
  gridColumn: FAILURE_TABLE_GRID_COLUMN.workflowThroughCount,
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
  minWidth: 0,
});

const NameLink = styled(Link)({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  color: 'inherit',
  '&:hover': {
    textDecoration: 'underline',
  },
});

const KindChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: '22px',
  padding: '0 10px',
  borderRadius: '11px',
  flexShrink: 0,
  backgroundColor: alpha(theme.palette.error.main, 0.14),
  color: theme.palette.error.main,
  fontSize: '0.75rem',
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: 'nowrap',
}));

const ChipDot = styled('span')(({ theme }) => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: theme.palette.error.main,
}));

const Leading = styled(Box)({
  gridColumn: FAILURE_TABLE_GRID_COLUMN.type,
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

const TriedMarker = styled(BodyText)({
  flexShrink: 0,
  fontSize: '0.75rem',
  whiteSpace: 'nowrap',
});

const ActionCell = styled(Box)({
  gridColumn: FAILURE_TABLE_GRID_COLUMN.action,
  display: 'flex',
  justifyContent: 'center',
});

const describeLastAttempt = ({ attemptedAt, requestedByEmail, batchSize }: LastRemediationAttempt) => {
  const when = formatDistanceToNow(new Date(attemptedAt), { addSuffix: true });
  const who = requestedByEmail ? ` by ${requestedByEmail}` : '';
  const batch = batchSize > 1 ? ` in a batch of ${batchSize}` : '';
  return `Retried ${when}${who}${batch}. It failed again.`;
};

const TimeText = styled(BodyText)({
  gridColumn: FAILURE_TABLE_GRID_COLUMN.mostRecent,
  whiteSpace: 'nowrap',
});

export const FailureRow = ({ failure }: { failure: RecentFailureItem }) => {
  const { getPath } = useTenants();

  return (
    <Row data-testid='recent-failure-row'>
      <Leading>
        <KindChip data-testid={`failure-kind-${failure.kind}`}>
          <ChipDot />
          {KIND_LABELS[failure.kind]}
        </KindChip>
        <FailureWhyButton assetId={failure.id} assetName={failure.name} kind={failure.kind} />
      </Leading>
      <NameCell>
        <NameLink to={getPath(failureLink(failure.kind, failure.id))} title={`View ${failure.name}`}>
          <BodyText>{failure.name}</BodyText>
        </NameLink>
        {failure.lastAttempt && (
          <Tooltip title={describeLastAttempt(failure.lastAttempt)}>
            <TriedMarker color='text.secondary' data-testid='failure-tried-marker'>
              Retried, failed again
            </TriedMarker>
          </Tooltip>
        )}
      </NameCell>
      <TimeText color='text.secondary'>{formatDistanceToNow(new Date(failure.failedAt), { addSuffix: true })}</TimeText>
      <ActionCell>
        <RowRemediationButton failure={failure} />
      </ActionCell>
    </Row>
  );
};
